import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Gate rendered above every private route on first login.
 * User must tick the checkbox and accept before the app is usable.
 * Persists via `/api/auth/accept-disclaimer` → sets `ai_disclaimer_accepted_at` on the user.
 */
export default function DisclaimerGate({ children }) {
  const { user, acceptDisclaimer } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || user.ai_disclaimer_accepted_at) return children;

  const accept = async () => {
    if (!checked) return toast.error("Please tick the acknowledgement first");
    setBusy(true);
    try {
      await acceptDisclaimer();
      toast.success("Thanks — enjoy GrantForge!");
    } catch {
      toast.error("Could not save your acknowledgement — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm p-6" data-testid="disclaimer-gate">
      <div className="max-w-lg w-full bg-card rounded-lg border border-primary/40 shadow-xl p-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-full bg-primary/15 text-primary flex items-center justify-center">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <div className="overline">One-time notice</div>
            <div className="font-display text-2xl leading-tight">About AI in GrantForge</div>
          </div>
        </div>
        <div className="text-sm text-foreground/90 space-y-3 mt-4 leading-relaxed">
          <p>
            GrantForge uses AI to draft, review, and score your grant proposals, papers, theses and consultancy documents. These outputs are <b>assistive suggestions</b> — not verified facts.
          </p>
          <p className="text-muted-foreground">
            AI may occasionally invent citations, misread agency rules, or over-simplify methodology. Please <b>always review, edit and verify</b> any generated content before submitting or publishing.
          </p>
        </div>
        <div className="mt-5 flex items-start gap-3 border-t border-border pt-4">
          <Checkbox id="ack" checked={checked} onCheckedChange={setChecked} data-testid="disclaimer-check" />
          <label htmlFor="ack" className="text-sm cursor-pointer select-none leading-relaxed">
            I understand that AI may make mistakes and I will check the content before finalising it.
          </label>
        </div>
        <div className="mt-6 flex justify-end">
          <Button
            onClick={accept}
            disabled={!checked || busy}
            className="rounded-full"
            data-testid="disclaimer-accept"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            {busy ? "Saving…" : "Acknowledge & enter GrantForge"}
          </Button>
        </div>
      </div>
    </div>
  );
}
